import { axiosInstance } from "./apiClient";

// ── Estrazione Bollette Acqua ────────────────────────────────────────────

export type WaterBillSessionStatus = "uploading" | "running" | "completed" | "error";
export type WaterBillFileStatus = "pending" | "processing" | "done" | "error";

export interface WaterBillFile {
  id: string;
  filename: string;
  status: WaterBillFileStatus;
  size_bytes: number;
  bills_count: number;
  alerts_count: number;
  error_message?: string | null;
  /** Il PDF è tenuto in hold e il file può essere rielaborato con "Riprova". */
  retryable?: boolean;
}

export interface WaterBillSession {
  id: string;
  status: WaterBillSessionStatus;
  created_by_name?: string | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  total_files: number;
  processed_files: number;
  failed_files: number;
  bills_count: number;
  alert_files: number;
  error_message?: string | null;
  excel_available: boolean;
  is_current: boolean;
  /** File falliti ancora rielaborabili (solo per l'ultima sessione, finché è in hold). */
  retryable_files?: number;
  retry_available?: boolean;
}

export interface WaterBillSessionDetail extends WaterBillSession {
  files: WaterBillFile[];
}

export interface WaterBillUploadResult {
  added: number;
  ignored: number;
  total_files: number;
  unreadable_archives: string[];
  errors: string[];
}

export interface WaterBillClient {
  id: string;
  code: string;
  site_name?: string | null;
  cdc?: string | null;
  company_name?: string | null;
  supplier?: string | null;
  city?: string | null;
  province?: string | null;
  meter_serial?: string | null;
  notes?: string | null;
}

export type WaterBillClientInput = Omit<WaterBillClient, "id">;

export async function getCurrentWaterBillSession(): Promise<WaterBillSessionDetail | null> {
  const response = await axiosInstance.get("/water-bills/sessions/current");
  return (response.data as WaterBillSessionDetail | null) ?? null;
}

export async function listWaterBillSessions(): Promise<WaterBillSession[]> {
  const response = await axiosInstance.get<WaterBillSession[]>("/water-bills/sessions");
  return response.data;
}

export async function createWaterBillSession(): Promise<WaterBillSessionDetail> {
  const response = await axiosInstance.post<WaterBillSessionDetail>("/water-bills/sessions");
  return response.data;
}

export interface UploadItem {
  file: File;
  /** Percorso relativo dentro la cartella caricata (o il nome del file). */
  path: string;
}

/** Carica un lotto di file nella sessione. `onProgress` riceve i byte inviati del lotto. */
export async function uploadWaterBillFiles(
  sessionId: string,
  items: UploadItem[],
  onProgress?: (loadedBytes: number) => void
): Promise<WaterBillUploadResult> {
  const form = new FormData();
  items.forEach((item) => {
    form.append("files", item.file, item.file.name);
    form.append("paths", item.path);
  });
  const response = await axiosInstance.post<WaterBillUploadResult>(
    `/water-bills/sessions/${encodeURIComponent(sessionId)}/files`,
    form,
    {
      timeout: 10 * 60 * 1000,
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => onProgress?.(e.loaded),
    }
  );
  return response.data;
}

export async function startWaterBillSession(sessionId: string): Promise<WaterBillSessionDetail> {
  const response = await axiosInstance.post<WaterBillSessionDetail>(
    `/water-bills/sessions/${encodeURIComponent(sessionId)}/start`
  );
  return response.data;
}

/** Interrompe l'elaborazione in corso: la sessione passa a "error" e i file non completati si possono riprovare. */
export async function stopWaterBillSession(sessionId: string): Promise<WaterBillSessionDetail> {
  const response = await axiosInstance.post<WaterBillSessionDetail>(
    `/water-bills/sessions/${encodeURIComponent(sessionId)}/stop`
  );
  return response.data;
}

/** Rielabora solo i file falliti dell'ultima sessione. */
export async function retryWaterBillSession(sessionId: string): Promise<WaterBillSessionDetail> {
  const response = await axiosInstance.post<WaterBillSessionDetail>(
    `/water-bills/sessions/${encodeURIComponent(sessionId)}/retry`
  );
  return response.data;
}

export async function deleteWaterBillSession(sessionId: string): Promise<void> {
  await axiosInstance.delete(`/water-bills/sessions/${encodeURIComponent(sessionId)}`);
}

export async function downloadWaterBillExcel(
  sessionId: string
): Promise<{ blob: Blob; filename: string }> {
  const response = await axiosInstance.get(
    `/water-bills/sessions/${encodeURIComponent(sessionId)}/excel`,
    { responseType: "blob", timeout: 2 * 60 * 1000 }
  );
  const disposition: string = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return { blob: response.data as Blob, filename: match ? match[1] : "Bollette_Acqua.xlsx" };
}

// ── Anagrafica clienti ───────────────────────────────────────────────────

export async function listWaterBillClients(): Promise<WaterBillClient[]> {
  const response = await axiosInstance.get<WaterBillClient[]>("/water-bills/clients");
  return response.data;
}

export async function createWaterBillClient(data: WaterBillClientInput): Promise<WaterBillClient> {
  const response = await axiosInstance.post<WaterBillClient>("/water-bills/clients", data);
  return response.data;
}

export async function updateWaterBillClient(
  id: string,
  data: WaterBillClientInput
): Promise<WaterBillClient> {
  const response = await axiosInstance.put<WaterBillClient>(
    `/water-bills/clients/${encodeURIComponent(id)}`,
    data
  );
  return response.data;
}

export async function deleteWaterBillClient(id: string): Promise<void> {
  await axiosInstance.delete(`/water-bills/clients/${encodeURIComponent(id)}`);
}

/** Messaggio di errore leggibile da una risposta axios. */
export function apiErrorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (err?.response?.status === 413) {
    return "Il file è troppo grande per essere caricato in un'unica richiesta.";
  }
  return fallback;
}
